import { useEffect, useState } from "react"
import { useSelector } from "@services/hooks/redux-hooks"
import moment, { Moment } from "moment"
import { Box, FormControl } from "@mui/material"

import EsfirusButton from "@components/ui/Button/EsfirusButton"

import { Obra } from "@models/obra"

import "./FormParte.scss"
import { getInternalCompany } from "@helpers/companyData"
import HeaderFormAviso from "./BBs/HeaderFormAviso/HeaderFormAviso"
import { erpService } from "@services/erp"
import { CustomerAddress } from "@models/customer-address"
import { NoticePriority, NoticeStatuses, NoticeTypes } from "@models/notices"
import { configurationService } from "@services/configApp"
import { UserWithDataParsed } from "@models/user"
import { Empresa } from "@models/empresa"
import AdjuntosContainer from "@screens/Avisos/BBs/AdjuntosContainer/AdjuntosContainer"

export interface FileAviso {
  name: string
  base64?: string
  document_id?: string | number
  unique_id?: string | number
}

export interface FormDataAviso {
  company: Empresa | null
  obra: Obra | void | null
  customerAddress: CustomerAddress | null
  notification: number;
  noticeType: NoticeTypes | null
  noticeStatus: NoticeStatuses | null
  noticePriority: NoticePriority | null
  date: Moment
  time: Moment
  responsibleEmployee: UserWithDataParsed | null
  targetEmployee: UserWithDataParsed | null
  originEmployee: UserWithDataParsed | null
  shortDescription: string
  comments: string
  files: FileAviso[]
  draft_id?: number | string
  notice_id?: number | string
  unique_id?: string
  customer?: any
  customerId?: number | null
}

export interface FormAvisoProps {
  editable: boolean
  isSaving?: boolean
  isValidating?: boolean
  initialData?: FormDataAviso | null
  isNewAviso?: boolean
  handleSave?: (a: FormDataAviso) => Promise<void>
  handleClose?: () => void
  handleValidate?: (a: FormDataAviso) => Promise<void>
}
  
  const FormAviso = ({ initialData, editable, isSaving, isValidating, isNewAviso, handleSave, handleClose, handleValidate }: FormAvisoProps) => {
    const { allowAvisosWithoutObra } = configurationService.getConfigAvisos();
    const empresas = useSelector(state => state.empresas.list)
  const noticesStatuses = useSelector(state => state.notices.listStatuses)
  const noticesPriorities = useSelector(state => state.notices.listPriorities)

  // Configuración de avisos del backoffice.
  const { statusByDefault, priorityByDefault, isSendAddressMandatory } = configurationService.getConfigAvisos();

  // Recopilación de toda la data del formulario.
  const [formData, setFormData] = useState<FormDataAviso>(initialData ?? {
    customerId: null,
    company: empresas.find(em => em.Empresa == getInternalCompany()) ?? null,
    customer: null,
    obra: null,
    customerAddress: null,
    noticeType: null,
    notification: 0,
    noticeStatus: noticesStatuses.find(ns => ns.status == statusByDefault) ?? null,
    noticePriority: noticesPriorities.find(np => np.priority == priorityByDefault) ?? null,
    date: moment(),
    time: moment(),
    responsibleEmployee: null,
    targetEmployee: null,
    originEmployee: null,
    shortDescription: "",
    comments: "",
    files: [],
  })

  // Carga de direcciones de clientes.
  const [loadingCustomerAddresses, setLoadingCustomerAddresses] = useState<boolean>(false)
  const [customerAddressesList, setCustomerAddressesList] = useState<CustomerAddress[]>([])

  // Actualización si cambia el código de cliente.
  useEffect(() => {
    const obra = formData.obra;
    const customer = formData.customer;

    const customerId = obra?.Cliente ?? customer?.customer_id;
    const branchId   = obra?.Delegacion ?? customer?.branch_id;
    const companyId  = obra?.Empresa ?? customer?.company_id;

    if (!customerId) return;

    fetchCustomerAddresses(customerId, branchId, companyId);
  }, [
    formData.obra?.Cliente,
    formData.obra?.Delegacion,
    formData.obra?.Empresa,
    formData.customer?.customer_id,
    formData.customer?.branch_id,
    formData.customer?.company_id,
  ]);

  // Validación para renderizar apartado de líneas o no
  const requiredFields = [
    formData.company?.Empresa,
    formData.noticeType?.notification_type,
    formData.noticeStatus?.status,
    formData.noticePriority?.priority,
    formData.date?.isValid(),
    formData.time?.isValid(),
    formData.responsibleEmployee?.id,
    formData.targetEmployee?.id,
    formData.originEmployee?.id,
    formData.shortDescription,
    formData.comments
  ];

  let dataIsOkey = requiredFields.every(Boolean);

  if (!allowAvisosWithoutObra) {
    dataIsOkey = dataIsOkey && !!(formData.obra?.obraCode || formData.obra?.Obra);
  }

  if (allowAvisosWithoutObra ) {
    dataIsOkey = dataIsOkey && !!formData.customer?.customer_id;
  }
  if (isSendAddressMandatory) {
    dataIsOkey = dataIsOkey && Boolean(formData.customerAddress?.address_id)
  }

  const fetchCustomerAddresses = async (customerId: number | string, branchId?: number, companyId?: number) => {
    setLoadingCustomerAddresses(true)

    try {
      const addressesResponse = await erpService.getCustomerAddresses(customerId, branchId, companyId)
      const addresses = addressesResponse.data.page_entries

      setCustomerAddressesList(addresses)
    } catch (error) {
      setCustomerAddressesList([])
    } finally {
      setLoadingCustomerAddresses(false)
    }
  }

  return (
    <FormControl style={{ gap: 12 }} className="parte-form" fullWidth>
      <HeaderFormAviso
        formData={formData}
        setFormData={setFormData}
        editable={(isNewAviso || editable) ?? false}
        customerAddressesData={{ loading: loadingCustomerAddresses, list: customerAddressesList }}
      />
      <AdjuntosContainer
        formData={formData}
        setFormData={setFormData}
        editable={editable}
        isView={!isNewAviso}
      />
      <Box className="ml-20 mr-20 mt-40 botonera">
        {handleClose && (
          <EsfirusButton
            className="new-parte-save-button"
            color="primary"
            variant="outlined"
            onClick={() => handleClose()}
            disabled={isSaving}
          >
            Cerrar
          </EsfirusButton>
        )}
        {handleSave && (
          <EsfirusButton
            className="new-parte-save-button"
            color="primary"
            onClick={async () => await handleSave(formData)}
            disabled={!dataIsOkey || isSaving}
          >
            {isSaving && <span style={{ height: 12, margin: 0 }} className="loader"></span>}
            Guardar
          </EsfirusButton>
        )}
        {handleValidate && (
          <EsfirusButton
            className="new-parte-save-button"
            color="secondary"
            onClick={async () => await handleValidate(formData)}
            disabled={!dataIsOkey || isValidating}
          >
            {isValidating && <span style={{ height: 12, margin: 0 }} className="loader"></span>}
            Traspasar
          </EsfirusButton>
        )}
      </Box>
    </FormControl>
  )
}

export default FormAviso
